import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Button,
  Space,
  Typography,
  Alert,
  Skeleton,
  Tooltip,
  message,
} from 'antd';
import {
  ExperimentOutlined,
  ReloadOutlined,
  SafetyOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  ClusterOutlined,
  EditOutlined,
  InboxOutlined,
  StarOutlined,
  AuditOutlined,
  ProfileOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import { enqueueValidationRun } from '@/api/validationRuns';
import { useStore } from '@/stores';
import { scenarioTags, type ScenarioSummary } from '@/api/scenarios';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill, type PillTone } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { HelpHotspot } from '@/features/help/HelpHotspot';
import { ScenarioDetailDrawer } from './ScenarioDetailDrawer';
import { ScenarioFilterBar } from './ScenarioFilterBar';
import { ScenarioEditorDrawer } from './ScenarioEditorDrawer';
import { RunTargetDrawer, type RunTargetRequest } from './RunTargetDrawer';
import { RunRequestsPanel } from './RunRequestsPanel';
import { EvaluationModelsDrawer } from './EvaluationModelsDrawer';
import { GenerateScenariosDrawer } from '@/features/scenario-intelligence/GenerateScenariosDrawer';
import { ProposalsDrawer } from '@/features/scenario-intelligence/ProposalsDrawer';
import { QuickReviewDrawer } from '@/features/scenario-intelligence/review/QuickReviewDrawer';
import { AdvancedScenarioEditorDrawer } from '@/features/scenario-intelligence/editor/AdvancedScenarioEditorDrawer';
import { ImproveScenarioDrawer } from '@/features/scenario-intelligence/improve/ImproveScenarioDrawer';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// ScenarioLibraryPage — catalog browser for the scenario library.
//
// Phase 2.A.5 layout:
//   PageHeader (title + helpTopic + count)
//   ScenarioFilterBar  ← agent picker + audience + turn count + categories +
//                        banned-phrase / skill-assertion toggles + tags + search
//   ScenarioGrid       ← clickable cards that open the detail drawer
//
// Replaces the previous two-column layout (left agent rail + chip strip).
// ─────────────────────────────────────────────────────────────────────────────

// Color tone per category code — used by the card's left border + code badge.
// Keys are NVARCHAR(8) codes from aivalidation.ScenarioCategory; we fall back
// to "slate" for codes we don't recognize so a new vertical with custom codes
// still renders cleanly.
const CATEGORY_TONE: Record<string, PillTone> = {
  A: 'red',
  B: 'amber',
  C: 'ink',
  D: 'violet',
  DR: 'violet',
  E: 'green',
  F: 'ink',
  G: 'red',
  H: 'ink',
  I: 'amber',
  J: 'ink',
  K: 'violet',
  L: 'slate',
  U: 'slate',
};

export const ScenarioLibraryPage = observer(function ScenarioLibraryPage() {
  const { scenariosStore: store } = useStore();

  // Editor drawer state — null+open=true means "new scenario"; a string
  // scenarioId means "edit this one in place." Lifted to the page so the
  // drawer can be reached from the page header AND the per-card edit button.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorScenarioId, setEditorScenarioId] = useState<string | null>(null);
  // Bumped after a run is asked for, so the run requests panel shows it at once.
  const [jobsVersion, setJobsVersion] = useState(0);

  useEffect(() => {
    if (store.items.length === 0 && !store.isLoading) {
      void store.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNewScenario = () => {
    setEditorScenarioId(null);
    setEditorOpen(true);
  };
  const openEditScenario = (scenarioId: string) => {
    setEditorScenarioId(scenarioId);
    setEditorOpen(true);
  };

  // ── "Run all scenarios" — batch trigger in the run drawer ──────────────
  // Real model credits are spent every time this runs; the drawer says so and shows the scenario count. Practice,
  // patient and agent are chosen there (founder, 31 October: practice first, then patient and agent).
  const [runAllOpen, setRunAllOpen] = useState(false);
  // D-SI-9: the Scenario / Critic / Evaluator model plan, opened from the header.
  const [modelsOpen, setModelsOpen] = useState(false);
  // SI-05a: Generate scenarios and its Proposals, reached from this header until the Scenario Intelligence workspace exists.
  const [generateOpen, setGenerateOpen] = useState(false);
  const [proposals, setProposals] = useState<{ open: boolean; key: string | null }>({ open: false, key: null });
  // Scenario Intelligence screens 05 and 10, reached from here until the workspace route exists.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [improveId, setImproveId] = useState<string | null>(null);
  const handleRunAll = () => {
    if (!store.params.agentId) {
      void message.warning('Pick an agent first — batch runs require one agent.');
      return;
    }
    setRunAllOpen(true);
  };

  const runAll = async ({ agentId, target, key }: RunTargetRequest): Promise<boolean> => {
    try {
      const resp = await enqueueValidationRun({
        agentId,
        triggerSource: 'manual',
        merchantId: target.merchantId,
        memberId: target.memberId,
        idempotencyKey: key,
      });
      void message.success(resp.message, 5);
      // A run of another agent chosen in the drawer: the library follows it, so its run requests are the ones shown.
      if (agentId !== store.params.agentId) store.setAgentFilter(agentId);
      setJobsVersion((v) => v + 1);
      return true;
    } catch (err) {
      // Review R10: the drawer stays open with the same key, so a retry cannot start a second run.
      void message.error('Failed to enqueue: ' + (err as Error).message + ' Retry from the drawer; it is the same request.');
      return false;
    }
  };

  const runAllDisabled = !store.params.agentId || store.isLoading;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<ExperimentOutlined />}
        title={
          <Space size={6} align="center">
            <span>Scenario library</span>
            <HelpHotspot topic="validations.scenarios.library" />
          </Space>
        }
        subtitle="Scripted conversations we replay against agents to make sure they still behave correctly. Pick an agent, slice with filters, and click any scenario to see its conversation and run history."
        count={store.activeFilterCount > 0 || store.search ? `${store.filteredCount} / ${store.totalCount}` : store.totalCount}
        countLabel={store.totalCount === 1 ? 'scenario' : 'scenarios'}
        actions={
          <Space>
            <Button icon={<PlusOutlined />} onClick={openNewScenario}>
              New scenario
            </Button>
            <Button icon={<ClusterOutlined />} onClick={() => setModelsOpen(true)}>
              Evaluation models
            </Button>
            <Button icon={<StarOutlined />} onClick={() => setGenerateOpen(true)}>
              Generate scenarios
            </Button>
            <Button
              icon={<InboxOutlined />}
              disabled={!store.params.agentId}
              title={store.params.agentId ? 'Generated proposals for the selected agent' : 'Pick an agent in the filter bar to see its proposals'}
              onClick={() => setProposals({ open: true, key: null })}
            >
              Proposals
            </Button>
            <Button
              icon={<AuditOutlined />}
              disabled={!store.params.agentId}
              title={store.params.agentId ? 'Decide on new and changed scenario versions' : 'Pick an agent in the filter bar to review its scenarios'}
              onClick={() => setReviewOpen(true)}
            >
              Review
            </Button>
            <Button
              icon={<PlayCircleOutlined />}
              type="primary"
              disabled={runAllDisabled}
              onClick={handleRunAll}
              title={
                runAllDisabled
                  ? 'Pick an agent in the filter bar to enable batch runs'
                  : 'Run all active scenarios for the selected agent'
              }
            >
              Run all scenarios
            </Button>
            <Button
              icon={<ReloadOutlined />}
              loading={store.isLoading}
              onClick={() => void store.fetch()}
            >
              Refresh
            </Button>
          </Space>
        }
      />

      {store.error && (
        <Alert type="error" message="Failed to load scenarios" description={store.error} showIcon closable />
      )}

      {/* One-row, slice-many-ways filter. The previous layout split this into
          a left rail (agent picker) plus a top filter strip plus a chip row;
          all three collapse into this single bar. */}
      <SectionCard noPadding>
        <ScenarioFilterBar />
      </SectionCard>

      {store.params.agentId != null && (
        <RunRequestsPanel agentId={store.params.agentId} refreshToken={jobsVersion} />
      )}

      {/* Catalog grid */}
      {store.isLoading && store.items.length === 0 ? (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SectionCard>
      ) : store.filtered.length === 0 ? (
        <SectionCard>
          <BrandedEmpty
            icon={<ExperimentOutlined />}
            title={store.activeFilterCount > 0 || store.search ? 'No scenarios match' : 'No scenarios on disk'}
            description={
              store.activeFilterCount > 0 || store.search
                ? 'Try clearing some filters or widening your search.'
                : 'Add JSON files under docs/agents/test-scenarios/ to see them here.'
            }
            action={
              store.activeFilterCount > 0 || store.search ? (
                <Button onClick={() => store.clearFilters()}>Clear all filters</Button>
              ) : undefined
            }
          />
        </SectionCard>
      ) : (
        <ScenarioGrid onEdit={openEditScenario} onEditDetails={setDetailsId} onImprove={setImproveId} />
      )}

      <ScenarioDetailDrawer />
      <EvaluationModelsDrawer open={modelsOpen} onClose={() => setModelsOpen(false)} />
      <GenerateScenariosDrawer
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
        agentId={store.params.agentId ?? null}
        onOpenProposals={(agentId, key) => {
          if (agentId !== store.params.agentId) store.setAgentFilter(agentId);
          setGenerateOpen(false);
          setProposals({ open: true, key });
        }}
      />
      <ProposalsDrawer
        open={proposals.open}
        onClose={() => {
          setProposals({ open: false, key: null });
          void store.fetch(); // an added draft is a new library scenario
        }}
        agentId={store.params.agentId ?? null}
        generationKey={proposals.key}
        onGenerateMore={() => {
          setProposals({ open: false, key: null });
          setGenerateOpen(true);
        }}
      />
      <QuickReviewDrawer
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        agentId={store.params.agentId ?? null}
        agentName={store.agentCanonicalNames.find((a) => a.agentId === store.params.agentId)?.canonicalName}
        onOpenScenario={setDetailsId}
      />
      <ImproveScenarioDrawer
        open={improveId != null}
        scenarioId={improveId}
        onClose={() => setImproveId(null)}
        onSaved={() => void store.fetch()}
      />
      <AdvancedScenarioEditorDrawer
        open={detailsId != null}
        scenarioId={detailsId}
        onClose={() => setDetailsId(null)}
        onSaved={() => void store.fetch()}
        onOpenScenario={setDetailsId}
      />
      {store.params.agentId != null && (
        <RunTargetDrawer
          open={runAllOpen}
          onClose={() => setRunAllOpen(false)}
          agentId={store.params.agentId}
          onRun={runAll}
        />
      )}
      <ScenarioEditorDrawer
        open={editorOpen}
        scenarioId={editorScenarioId}
        defaultAgentId={store.params.agentId}
        defaultDomain="oncology"
        onClose={() => setEditorOpen(false)}
        onSaved={() => store.fetch()}
        onTagsSaved={() => void store.fetch()}
      />
    </div>
  );
});

// ── Scenario grid ────────────────────────────────────────────────────────────

const ScenarioGrid = observer(function ScenarioGrid({
  onEdit,
  onEditDetails,
  onImprove,
}: {
  onEdit: (scenarioId: string) => void;
  onEditDetails: (scenarioId: string) => void;
  onImprove: (scenarioId: string) => void;
}) {
  const { scenariosStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
        gap: 12,
      }}
    >
      {store.filtered.map((s) => (
        <ScenarioCard key={s.filePath} scenario={s} isDark={isDark} onEdit={onEdit} onEditDetails={onEditDetails} onImprove={onImprove} />
      ))}
    </div>
  );
});

const ScenarioCard = observer(function ScenarioCard({
  scenario,
  isDark,
  onEdit,
  onEditDetails,
  onImprove,
}: {
  scenario: ScenarioSummary;
  isDark: boolean;
  onEdit: (scenarioId: string) => void;
  onEditDetails: (scenarioId: string) => void;
  onImprove: (scenarioId: string) => void;
}) {
  const { scenariosStore: store } = useStore();

  // Card accent color — sourced from the category-tone map. Unknown codes
  // (a new vertical's "ESC" or "DR2") fall back to slate; the card still
  // renders, just with a neutral edge.
  const tone = CATEGORY_TONE[scenario.category] ?? 'slate';
  const fg =
    tone === 'red' ? colors.red[600]
      : tone === 'amber' ? colors.amber[600]
      : tone === 'green' ? colors.green[600]
      : tone === 'violet' ? colors.violet[600]
      : tone === 'ink' ? colors.ink[600]
      : colors.slate[500];

  // Prefer agent display name (human-readable) over canonical name (file slug)
  // when both are available. Falls back to the canonical so we never show a
  // blank meta line.
  const agentLabel = scenario.agentDisplayName ?? scenario.agentCanonicalName;

  return (
    <button
      type="button"
      onClick={() => {
        trackEvent('Scenario.Opened', { scenarioId: scenario.scenarioId });
        void store.openByPath(scenario.filePath);
      }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        padding: '14px 16px',
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderLeft: `3px solid ${fg}`,
        borderRadius: 10,
        cursor: 'pointer',
        gap: 8,
        minHeight: 120,
        transition: 'border-color 120ms ease, transform 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = fg;
        e.currentTarget.style.borderLeftColor = fg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = isDark ? colors.slate[700] : colors.slate[200];
        e.currentTarget.style.borderLeftColor = fg;
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minWidth: 0 }}>
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            fontWeight: 700,
            background: fg,
            color: '#FFFFFF',
            padding: '2px 7px',
            borderRadius: 5,
            flexShrink: 0,
          }}
        >
          {scenario.category}
        </span>
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 13,
            fontWeight: 600,
            color: isDark ? colors.slate[50] : colors.slate[900],
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flex: 1,
            minWidth: 0,
          }}
        >
          {scenario.scenarioId}
        </Text>
        <Tooltip title="Improve with AI: the Scenario model proposes lines; you keep or drop each, then save a new version">
          <Button
            type="text"
            size="small"
            icon={<BulbOutlined />}
            aria-label={`Improve ${scenario.scenarioId} with AI`}
            onClick={(e) => {
              e.stopPropagation();
              onImprove(scenario.scenarioId);
            }}
          />
        </Tooltip>
        <Tooltip title="Edit details: context, timeline, expectations, checks and provenance">
          <Button
            type="text"
            size="small"
            icon={<ProfileOutlined />}
            aria-label={`Edit details of ${scenario.scenarioId}`}
            onClick={(e) => {
              e.stopPropagation();
              onEditDetails(scenario.scenarioId);
            }}
          />
        </Tooltip>
        <Tooltip title="Edit with AI co-pilot">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(scenario.scenarioId);
            }}
          />
        </Tooltip>
      </div>
      {scenario.description && (
        <div
          style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            color: isDark ? colors.slate[300] : colors.slate[600],
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {scenario.description}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10.5,
            color: isDark ? colors.slate[500] : colors.slate[400],
          }}
        >
          {agentLabel}
        </Text>
        {scenario.audience && (
          <>
            <span style={{ width: 3, height: 3, borderRadius: 999, background: colors.slate[400] }} />
            <Text style={{ fontSize: 11.5, color: isDark ? colors.slate[400] : colors.slate[500] }}>
              {scenario.audience}
            </Text>
          </>
        )}
        <span style={{ width: 3, height: 3, borderRadius: 999, background: colors.slate[400] }} />
        <Text style={{ fontSize: 11.5, color: isDark ? colors.slate[400] : colors.slate[500] }}>
          {scenario.turnCount} {scenario.turnCount === 1 ? 'turn' : 'turns'}
        </Text>
        {scenario.isMultiTurn && <StatusPill tone="violet">Multi-turn</StatusPill>}
        {scenario.isSafety && (
          <StatusPill tone="red" icon={<SafetyOutlined />}>
            Safety
          </StatusPill>
        )}
        {scenario.isAdversarial && <StatusPill tone="amber">Red-team</StatusPill>}
        {scenarioTags(scenario).map((tag) => (
          <StatusPill key={tag} tone="slate">{tag}</StatusPill>
        ))}
      </div>
    </button>
  );
});
